import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Container, Grid, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomSwitch from "../../../components/CustomSwitch";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import {
  createBinWoLoader,
  createLocation,
  deleteLocationById,
  getLocations,
  updateLocationActiveById,
  updateLocationById,
} from "../../../redux/reducers/clinicSlice";
import { isActiveRecord } from "../../../util/activeStatus";
import { AppColors } from "../../../util/AppColors";
import { delBtn, plusBtn } from "../../../util/CommonIconStyle";
import { locationCategoryList, locationTypes } from "../../../util/dropList";
import { patchMandateSession } from "../../../util/mandateSessionStorage";
import { canWriteModule } from "../../../util/roleModuleAccess";

const initValues = {
  locationName: "",
  locationType: null,
  category: null,
};
const initErrs = {
  locationName: false,
  locationType: false,
  category: false,
};
const initHlps = {
  locationName: "",
  locationType: "",
  category: "",
};

const tableHeaders = [
  "locationName",
  "locationType",
  "category",
  "activeStatus",
  "extraStatus",
];

const toLocationList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const Location = ({ activeSubTab = "Location" }) => {
  const dispatch = useDispatch();
  const fetchRef = useRef(false);
  const canManageInventory = canWriteModule("inventory");
  const [searchValue, setSearchValue] = useState("");
  const [modVsble, setModVsble] = useState(false);
  const [delModVsble, setDelModVsble] = useState(false);
  const [statusModVsble, setStatusModVsble] = useState(false);
  const [locationValues, setLocationValues] = useState(initValues);
  const [locationErrors, setLocationErrors] = useState(initErrs);
  const [locationHelps, setLocationHelps] = useState(initHlps);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [pendingActiveStatus, setPendingActiveStatus] = useState(null);
  const { clinicRoles, locationList } = useSelector((state) => state?.clinic);
  const role = localStorage.getItem("role");
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { dataSharing, locationCategoryOptions } = useMemo(() => {
    if (!mandateDataRaw) {
      return {
        dataSharing: {},
        locationCategoryOptions: locationCategoryList,
      };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      const categories = Array.isArray(reqData?.categories)
        ? reqData.categories
        : [];
      const locationCategories = categories?.filter(
        (loc) => loc?.type === "location" && loc?.name,
      );
      const fromMandate = locationCategories
        ?.filter((loc) => isActiveRecord(loc))
        ?.map((loc) => ({
          name: loc.name,
          value: loc.name,
        }));
      const inactiveNames = new Set(
        locationCategories
          ?.filter((loc) => !isActiveRecord(loc))
          ?.map((loc) => loc?.name?.toLowerCase())
          ?.filter(Boolean),
      );
      const seen = new Set(fromMandate.map((li) => li?.value?.toLowerCase()));
      const fromStatic = locationCategoryList.filter(
        (li) =>
          !seen.has(li?.value?.toLowerCase()) &&
          !inactiveNames.has(li?.value?.toLowerCase()),
      );
      const merged =
        fromMandate.length > 0 || inactiveNames.size > 0
          ? [...fromMandate, ...fromStatic]
          : locationCategoryList;
      return {
        dataSharing: reqData?.dataSharing ?? {},
        locationCategoryOptions: merged,
      };
    } catch {
      return {
        dataSharing: {},
        locationCategoryOptions: locationCategoryList,
      };
    }
  }, [mandateDataRaw]);

  useEffect(() => {
    onLocationApiCall();
  }, [dispatch]);

  const onLocationApiCall = (url) => {
    try {
      const request = url
        ? dispatch(getLocations(url))
        : dispatch(getLocations());
      request.then((res) => {
        try {
          const payload = res?.payload;
          if (payload == null) return;
          patchMandateSession({
            locations: toLocationList(payload),
          });
        } catch {
          // ignore mandate patch errors
        }
      });
    } catch {
      // ignore location refresh errors
    }
  };

  const handleCreate = () => {
    setModVsble(true);
  };

  const handleModClose = () => {
    setModVsble(false);
    setLocationValues(initValues);
    setLocationErrors(initErrs);
    setLocationHelps(initHlps);
    setSelectedLocation(null);
  };

  const handleChange = (name, value) => {
    setLocationValues({ ...locationValues, [name]: value });
    setLocationErrors({ ...locationErrors, [name]: value?.length === 0 });
    setLocationHelps({
      ...locationHelps,
      [name]: value?.length === 0 ? "This is required field" : "",
    });
  };

  const handleEdit = (loc) => {
    const reqObj = {
      ...locationValues,
      locationName: loc?.locationName,
      locationType: loc?.locationType,
    };
    if (loc?.category) {
      reqObj.category = loc?.category;
    }
    setLocationValues(reqObj);
    setSelectedLocation(loc);
    setModVsble(true);
  };

  const handleDelete = (loc) => {
    setSelectedLocation(loc);
    setDelModVsble(true);
  };

  const handleChangeSwitch = (loc) => {
    if (!loc?.id) return;
    setSelectedLocation(loc);
    setPendingActiveStatus(!isActiveRecord(loc));
    setStatusModVsble(true);
  };

  const handleCloseStatusMod = () => {
    setStatusModVsble(false);
    setPendingActiveStatus(null);
    setSelectedLocation(null);
  };

  const handleConfirmStatusUpdate = () => {
    if (!selectedLocation?.id || pendingActiveStatus == null) return;
    dispatch(
      updateLocationActiveById({
        locationId: selectedLocation.id,
        active: pendingActiveStatus === true,
      }),
    ).then((res) => {
      if (res?.payload) {
        onLocationApiCall();
        handleCloseStatusMod();
      }
    });
  };

  const minLocationId = Math.min(
    ...(locationList
      ?.map((loc) => Number(loc?.id))
      ?.filter((id) => !isNaN(id)) ?? [Infinity]),
  );

  const getTableLocations = () => {
    return locationList
      ?.filter((loc) =>
        loc?.locationName?.toLowerCase()?.includes(searchValue?.toLowerCase()),
      )
      ?.map((loc) => ({
        ...loc,
        locationType: loc?.locationType,
        category: loc?.category ?? "-",
        activeStatus: (
          <div className="flex-row-ali-cen">
            {canManageInventory ? (
              <CustomSwitch
                value={isActiveRecord(loc)}
                onChange={() => handleChangeSwitch(loc)}
                greenToGray
              />
            ) : null}
            <div
              style={{ width: 80 }}
              className={canManageInventory ? "ml10" : ""}
            >
              {isActiveRecord(loc) ? (
                <Typography className="green4 grn-bck status-card fs12 font-medium">
                  Active
                </Typography>
              ) : (
                <Typography className="red11 red-back status-card fs12 font-medium">
                  Inactive
                </Typography>
              )}
            </div>
          </div>
        ),
        extraStatus: canManageInventory ? (
          <div className="flex-row">
            {Number(loc?.id) !== minLocationId ? (
              <div className="cursor" onClick={() => handleDelete(loc)}>
                <div className="flex-center">
                  <DeleteOutlineIcon sx={delBtn} />
                </div>
                <Typography className="red3 fs8 txt-semi-bold text-align-center">
                  Delete
                </Typography>
              </div>
            ) : null}
            <div
              className={`cursor ${Number(loc?.id) !== minLocationId ? "ml10" : ""}`}
              onClick={() => handleEdit(loc)}
            >
              <div className="flex-center">
                <EditIcon sx={plusBtn} />
              </div>
              <Typography className="blue-color fs8 txt-semi-bold text-align-center">
                Edit
              </Typography>
            </div>
          </div>
        ) : null,
      }));
  };

  const handleValidate = () => {
    let errObj = {};
    let hlpObj = {};
    Object.keys(locationValues).forEach((key) => {
      if (locationValues?.[key]?.length === 0 || !locationValues?.[key]) {
        errObj = {
          ...errObj,
          [key]:
            key === "category"
              ? locationValues?.locationType?.includes("Storage")
                ? true
                : false
              : true,
        };
        hlpObj = {
          ...hlpObj,
          [key]:
            key === "category"
              ? locationValues?.locationType?.includes("Storage")
                ? "This is required field"
                : ""
              : "This is required field",
        };
      }
    });
    setLocationErrors({ ...locationErrors, ...errObj });
    setLocationHelps({ ...locationHelps, ...hlpObj });
    return Object.values(errObj)?.some((err) => err === true);
  };

  const handleSave = () => {
    if (handleValidate()) return;
    const data = {
      locationName: locationValues?.locationName,
      locationType: locationValues?.locationType,
    };
    if (locationValues?.locationType?.includes("Storage")) {
      data.category = locationValues?.category;
    }
    if (selectedLocation) {
      dispatch(
        updateLocationById({ locationId: selectedLocation?.id, data }),
      ).then((res) => {
        if (res?.payload) {
          onLocationApiCall();
          handleModClose();
        }
      });
    } else {
      dispatch(createLocation(data)).then((res) => {
        if (res?.payload) {
          const locationId = res?.payload?.id;
          if (locationId != null) {
            dispatch(
              createBinWoLoader({
                name: locationValues?.locationName,
                locationId,
                isDefault: true,
              }),
            );
          }
          onLocationApiCall();
          handleModClose();
        }
      });
    }
  };

  const handleCloseDelMod = () => {
    setDelModVsble(false);
    setSelectedLocation(null);
  };

  const handleDeleteLocation = () => {
    dispatch(deleteLocationById(selectedLocation?.id)).then((res) => {
      if (res?.payload) {
        onLocationApiCall();
        handleCloseDelMod();
      }
    });
  };

  return (
    <Container maxWidth="xl">
      <div className="back-white pv10">
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          xl={12}
          className="flex-row-center"
        >
          <div className="flex-row">
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              style={{ width: "400px" }}
            >
              <CustomTextField
                placeholder="Search"
                fullWidth
                backgroundColor={AppColors.lightBlue2}
                value={searchValue}
                handleChange={(e) => setSearchValue(e.target.value)}
              />
            </Grid>
          </div>
          {canManageInventory ? (
            <div className="w5Per ml20">
              <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
                <div>
                  <CustomButton text="Create" smallBtn onClick={handleCreate} />
                </div>
              </Grid>
            </div>
          ) : null}
        </Grid>

        <CustomTable
          columns={tableHeaders}
          datas={getTableLocations()}
          grey
          maxHeight="68vh"
        />
      </div>

      <CustomModal
        open={modVsble}
        onClose={handleModClose}
        header={selectedLocation ? "Edit" : "Create"}
        modalWidth={30}
        rightModal
      >
        <Grid container spacing={2} className="ph20">
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            {/* <div className="txt-semi-bold fs14 light-grey">
              Location Name <span className="red-color">*</span>
            </div> */}
            <div>
              <CustomTextField
                name="locationName"
                fullWidth
                value={locationValues?.locationName}
                error={locationErrors?.locationName}
                helperText={locationHelps?.locationName}
                handleChange={(e) => {
                  const val = e?.target?.value?.replace(/[^a-zA-Z0-9 ]/g, "");
                  handleChange("locationName", val);
                }}
                label="Location Name"
                required={true}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            {/* <div className="txt-semi-bold fs14 light-grey">
              Location Type <span className="red-color">*</span>
            </div> */}
            <div className="mt5">
              <CustomSelect
                list={locationTypes}
                value={locationValues?.locationType}
                error={locationErrors?.locationType}
                label="Location Type *"
                helperText={locationHelps?.locationType}
                handleChange={(e) =>
                  handleChange("locationType", e?.target?.value)
                }
                select
                disabled={!!selectedLocation?.id}
              />
            </div>
          </Grid>
          {locationValues?.locationType?.includes("Storage") ? (
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="mt5">
                <CustomSelect
                  list={locationCategoryOptions}
                  value={locationValues?.category}
                  error={locationErrors?.category}
                  label="Category *"
                  helperText={locationHelps?.category}
                  handleChange={(e) =>
                    handleChange("category", e?.target?.value)
                  }
                  select
                />
              </div>
            </Grid>
          ) : null}
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="flex1-end mt10">
              <div>
                <CustomButton
                  text={selectedLocation ? "Update" : "Submit"}
                  smallBtn
                  onClick={handleSave}
                />
              </div>
            </div>
          </Grid>
        </Grid>
      </CustomModal>

      <CustomModal
        open={delModVsble}
        onClose={handleCloseDelMod}
        header="Delete Location"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to delete the supplier?
        </Typography>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <div className="clinic-mod-btn-pos mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                redBtn
                onClick={handleDeleteLocation}
                smallBtn
              />
            </div>
            <div className="ml10">
              <CustomButton text="No" onClick={handleCloseDelMod} smallBtn />
            </div>
          </div>
        </Grid>
      </CustomModal>

      <CustomModal
        open={statusModVsble}
        onClose={handleCloseStatusMod}
        header="Update Status"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to update the status?
        </Typography>
        <Typography className="txt-regular fs14 ml10 mt10">
          This will affect the existing inventory configurations.
        </Typography>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <div className="flex1-center mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                onClick={handleConfirmStatusUpdate}
                smallBtn
                redBtn
              />
            </div>
            <div className="ml10">
              <CustomButton text="No" onClick={handleCloseStatusMod} smallBtn />
            </div>
          </div>
        </Grid>
      </CustomModal>
    </Container>
  );
};

export default Location;
